/**
 * Low-level geometry primitives and the resize-parameter model shared by the
 * page-object transforms and the snap-guide machinery: point rotation,
 * axis-aligned bounding boxes, rotated corner derivation, axis-coordinate
 * scaling, the per-handle resize direction table, and the snap-free helpers
 * built on them (rotated bounding-box rects, coordinate-scale factories, and
 * the croppable/sticker predicates).
 *
 * Leaf module (no sibling imports) so `geometry.ts` and `snapping.ts` can both
 * depend on it without an import cycle, and so consumers that only need these
 * helpers do not pull in the interactive transform and snapping machinery.
 */

import type { EditorInteractivePageObjectResizeSide } from '@sosocio/creator-types/app';
import type {
	PageModel,
	PageObjectModel,
} from '@sosocio/creator-types/project';

/**
 * The index of the fixed corner (0-3) that remains unchanged during resizing
 * 		- 0: Top-left
 * 		- 1: Top-right
 * 		- 2: Bottom-right
 * 		- 3: Bottom-left
 */
export type FixedCornerIndex = 0 | 1 | 2 | 3;

export interface Point {
	x: number;
	y: number;
}

/**
 * 1 for increase, -1 for decrease, 0 for no change
 */
type ResizeDirection = -1 | 0 | 1;

interface ResizeParameter {
	/**
	 * The index of the fixed corner/side that remains unchanged during resizing
	 */
	fixedCornerIndex: FixedCornerIndex;
	/**
	 * Increase, decrease or no change on the height resize
	 */
	heightDirection: ResizeDirection;
	/**
	 * Increase, decrease or no change on the width resize
	 */
	widthDirection: ResizeDirection;
}

/**
 * Parameters to indicate the direction of the height and width adjustment
 * when resizing an object and also the fixed corner/size to remain unchanged
 */
type ResizeParameters = {
	[side in EditorInteractivePageObjectResizeSide]: ResizeParameter;
};

export interface BoundingBox {
	minX: number;
	maxX: number;
	minY: number;
	maxY: number;
}

export const DEGREES_TO_RADIANS = Math.PI / 180;

/**
 * Resize parameters for each corner and side of the object
 */
export const resizeParameters: ResizeParameters = {
	/**
	 * Corner `bottom-right` fixed to `top-left` corner
	 */
	'bottom-right': {
		widthDirection: 1,
		heightDirection: 1,
		fixedCornerIndex: 0,
	},
	/**
	 * Corner `top-left` fixed to `bottom-right` corner
	 */
	'top-left': {
		widthDirection: -1,
		heightDirection: -1,
		fixedCornerIndex: 2,
	},
	/**
	 * Corner `top-right` fixed to `bottom-left` corner
	 */
	'top-right': {
		widthDirection: 1,
		heightDirection: -1,
		fixedCornerIndex: 3,
	},
	/**
	 * Corner `bottom-left` fixed to `top-right` corner
	 */
	'bottom-left': {
		widthDirection: -1,
		heightDirection: 1,
		fixedCornerIndex: 1,
	},
	/**
	 * Side `bottom` fixed to `top` side, since the width it's not changing
	 * fixing to the `top-left` corner is valid
	 */
	bottom: {
		widthDirection: 0,
		heightDirection: 1,
		fixedCornerIndex: 0,
	},
	/**
	 * Side `top` fixed to `bottom` side, since the width it's not changing
	 * fixing to the `bottom-right` corner is valid
	 */
	top: {
		widthDirection: 0,
		heightDirection: -1,
		fixedCornerIndex: 2,
	},
	/**
	 * Side `right` fixed to `left` side, since the height it's not changing
	 * fixing to the `top-left` corner is valid
	 */
	right: {
		widthDirection: 1,
		heightDirection: 0,
		fixedCornerIndex: 0,
	},
	/**
	 * Side `left` fixed to the `right` side, since the height it's not changing
	 * fixing to the `top-right` corner is valid
	 */
	left: {
		widthDirection: -1,
		heightDirection: 0,
		fixedCornerIndex: 1,
	},
};

/**
 * Calculate the bounding box of rotated points
 * @param points Array of points
 * @returns The bounding box of the points
 */
export function getBoundingBox(points: Point[]): BoundingBox {
	const xs = points.map((p) => p.x);
	const ys = points.map((p) => p.y);

	return {
		minX: Math.min(...xs),
		maxX: Math.max(...xs),
		minY: Math.min(...ys),
		maxY: Math.max(...ys),
	};
}

/**
 * Get the rotated corners of an object model
 * @param objectModel The object model to get corners from
 * @returns The rotated corners of the object model
 */
export function getRotatedCorners(objectModel: OptionalExceptFor<PageObjectModel, 'height' | 'rotate' | 'width' | 'x_axis' | 'y_axis' >): Point[] {
	const {
		x_axis,
		y_axis,
		width,
		height,
		rotate,
	} = objectModel;
	const cx = x_axis + width / 2;
	const cy = y_axis + height / 2;
	/**
	 * Corner order matches {@link FixedCornerIndex}.
	 */
	const points = [
		{
			x: x_axis,
			y: y_axis,
		},
		{
			x: x_axis + width,
			y: y_axis,
		},
		{
			x: x_axis + width,
			y: y_axis + height,
		},
		{
			x: x_axis,
			y: y_axis + height,
		},
	];

	return points.map((p) => (

		rotatePoint(
			cx,
			cy,
			p.x,
			p.y,
			rotate,
		)
	));
}

/**
 * Rotate a point around a center point by a given angle
 * @param cx Center x coordinate
 * @param cy Center y coordinate
 * @param px Point x coordinate
 * @param py Point y coordinate
 * @param angle Angle in degrees
 * @returns Rotated point
 */
function rotatePoint(
	cx: number,
	cy: number,
	px: number,
	py: number,
	angle: number,
): Point {
	if (angle === 0) {
		return {
			x: px,
			y: py,
		};
	}

	const rad = angle * DEGREES_TO_RADIANS;
	const cos = Math.cos(rad);
	const sin = Math.sin(rad);
	const dx = px - cx;
	const dy = py - cy;

	return {
		x: cx + dx * cos - dy * sin,
		y: cy + dx * sin + dy * cos,
	};
}

/**
 * Scales a coordinate that uses the trim origin (0,0) while accounting for bleed segments
 * that may change independently from the trim size. Authored coordinates beyond the
 * outer bleed bounds extrapolate from the destination bleed edge at the trim scale;
 * without source bleed, the entire coordinate space uses the trim scale.
 *
 * @param value Coordinate to transform from the source space.
 * @param sourceDimension Trim dimension (width or height) of the source page.
 * @param sourceBleed Bleed margin of the source page.
 * @param targetDimension Trim dimension (width or height) of the target page.
 * @param targetBleed Bleed margin of the target page.
 * @returns Coordinate expressed in the target page space.
 */
export function scaleAxisCoordinate(
	value: number,
	sourceDimension: number,
	sourceBleed: number,
	targetDimension: number,
	targetBleed: number,
): number {
	if (
		sourceDimension > 0
		&& Number.isFinite(sourceDimension)
		&& Number.isFinite(targetDimension)
	) {
		const trimScale = targetDimension / sourceDimension;

		if (!sourceBleed) {
			return value * trimScale;
		}

		if (value < -sourceBleed) {
			return -targetBleed + (value + sourceBleed) * trimScale;
		}

		if (value > sourceDimension + sourceBleed) {
			return targetDimension + targetBleed + (value - sourceDimension - sourceBleed) * trimScale;
		}
	}

	/**
	 * Negative or zero coordinates live inside the bleed region, so we keep
	 * them as a proportional offset of the bleed thickness rather than
	 * scaling them against the trim width/height.
	 */
	if (value <= 0) {
		if (
			!sourceBleed
			|| !targetBleed
		) {
			return 0;
		}

		return (value / sourceBleed) * targetBleed;
	}

	/**
	 * Coordinates beyond the trim edge represent the trailing bleed margin.
	 * We convert the overflow (distance past the trim) so it matches the
	 * destination bleed thickness.
	 */
	if (value >= sourceDimension) {
		const overflow = value - sourceDimension;

		if (
			!sourceBleed
			|| !targetBleed
		) {
			return targetDimension;
		}

		return targetDimension + (overflow / sourceBleed) * targetBleed;
	}

	/**
	 * Points inside the trim area scale proportionally using the trim sizes
	 * because they do not interact with bleed adjustments.
	 */
	return (value / sourceDimension) * targetDimension;
}

interface ScaleTarget {
	height: number;
	width: number;
}

export interface BoundingBoxRect {
	height: number;
	width: number;
	x: number;
	y: number;
}

export const MINIMUM_VISIBLE_PIXELS = 50;

/**
 * Calculates the axis-aligned {@link BoundingBoxRect} that encloses a rotated
 * {@link PageObjectModel}. The computation derives the object's rotated corner
 * positions and then determines the minimum and maximum coordinates across both
 * axes to build the bounding rectangle.
 *
 * @param objectModel {@link PageObjectModel} containing the `x_axis`, `y_axis`,
 * `width`, `height`, and `rotate` data required to derive the rotated corners.
 * @returns {@link BoundingBoxRect} describing the enclosing rectangle in page
 * coordinates.
 */
export function calculateRotatedBoundingBox(objectModel: OptionalExceptFor<PageObjectModel, 'height' | 'rotate' | 'width' | 'x_axis' | 'y_axis' >): BoundingBoxRect {
	const boundingBox = getBoundingBox(getRotatedCorners(objectModel));

	return {
		height: boundingBox.maxY - boundingBox.minY,
		width: boundingBox.maxX - boundingBox.minX,
		x: boundingBox.minX,
		y: boundingBox.minY,
	};
}

/**
 * Scales a horizontal coordinate using the current page dimensions.
 *
 * @param value Horizontal coordinate to scale.
 * @returns Scaled coordinate.
 */
export function createScaleHorizontalCoordinateFn(
	sourceWidth: number,
	sourceOffset: number,
	target: OptionalExceptFor<ScaleTarget, 'width'>,
	targetOffset: number,
): (value: number) => number {
	return (value) => scaleAxisCoordinate(
		value,
		sourceWidth,
		sourceOffset,
		target.width,
		targetOffset,
	);
}

/**
 * Scales a vertical coordinate using the current page dimensions.
 *
 * @param value Vertical coordinate to scale.
 * @returns Scaled coordinate.
 */
export function createScaleVerticalCoordinateFn(
	sourceHeight: number,
	sourceOffset: number,
	target: OptionalExceptFor<ScaleTarget, 'height'>,
	targetOffset: number,
): (value: number) => number {
	return (value) => scaleAxisCoordinate(
		value,
		sourceHeight,
		sourceOffset,
		target.height,
		targetOffset,
	);
}

type PhotoCrop = Pick<PageObjectModel, 'cropheight' | 'cropwidth' | 'cropx' | 'cropy'>;

/**
 * Adapts an existing photo crop to a resized frame so the user's framing
 * survives offering changes. The crop keeps its center and grows along the
 * short side to match the frame ratio (everything that was visible stays
 * visible), shrinking uniformly only when that would exceed the photo.
 *
 * @param crop Current crop, in photo pixels.
 * @param frame New object frame dimensions.
 * @param photo Full photo dimensions, in photo pixels.
 * @returns Crop matching the frame ratio, kept within the photo bounds.
 */
export function fitPhotoCropToFrame(
	crop: PhotoCrop,
	frame: ScaleTarget,
	photo: ScaleTarget,
): PhotoCrop {
	const frameRatio = frame.width / frame.height;
	let cropwidth = crop.cropwidth;
	let cropheight = crop.cropheight;

	if (frameRatio > cropwidth / cropheight) {
		cropwidth = cropheight * frameRatio;
	} else {
		cropheight = cropwidth / frameRatio;
	}

	const shrinkFactor = Math.min(
		1,
		photo.width / cropwidth,
		photo.height / cropheight,
	);
	cropwidth *= shrinkFactor;
	cropheight *= shrinkFactor;

	const centerX = crop.cropx + crop.cropwidth / 2;
	const centerY = crop.cropy + crop.cropheight / 2;

	return {
		cropx: Math.max(
			0,
			Math.min(
				centerX - cropwidth / 2,
				photo.width - cropwidth,
			),
		),
		cropy: Math.max(
			0,
			Math.min(
				centerY - cropheight / 2,
				photo.height - cropheight,
			),
		),
		cropwidth,
		cropheight,
	};
}

type PhotoFrame = Pick<PageObjectModel, 'height' | 'width' | 'x_axis' | 'y_axis'>;

/**
 * Shrinks a photo frame around its center so its crop is not enlarged beyond
 * `maxScale` page pixels per photo pixel, mirroring the minimum-resolution
 * sizing of `Template.fitPhotoInRectangle`.
 *
 * @param frame Photo object frame, in page pixels.
 * @param crop Crop shown in the frame, in photo pixels (same ratio as the frame).
 * @param maxScale Maximum enlargement, the offering's `configdpi / minimumdpi`.
 * @returns Frame within the enlargement limit, sharing the original center.
 */
export function fitFrameToCropResolution(
	frame: PhotoFrame,
	crop: Pick<PhotoCrop, 'cropheight' | 'cropwidth'>,
	maxScale: number,
): PhotoFrame {
	const shrinkFactor = Math.min(
		1,
		(crop.cropwidth * maxScale) / frame.width,
		(crop.cropheight * maxScale) / frame.height,
	);
	const width = frame.width * shrinkFactor;
	const height = frame.height * shrinkFactor;

	return {
		x_axis: frame.x_axis + (frame.width - width) / 2,
		y_axis: frame.y_axis + (frame.height - height) / 2,
		width,
		height,
	};
}

/**
 * Computes the minimum allowed {@link BoundingBox} for a {@link PageObjectModel}
 * so it remains at least `minVisiblePixels` visible within the containing
 * {@link PageModel}. The calculation caps the minimum visibility to the object's
 * own dimensions to avoid negative offsets on small objects.
 *
 * @param objectModel {@link PageObjectModel} supplying the width and height used
 * to derive visibility thresholds.
 * @param pageModel {@link PageModel} providing the page width and height limits.
 * @param minVisiblePixels Minimum number of pixels that must stay visible on each
 * edge before the object is considered out of bounds. Defaults to
 * {@link MINIMUM_VISIBLE_PIXELS}.
 * @returns {@link BoundingBox} describing the min/max x/y coordinates that keep
 * the object within the page while respecting visibility requirements.
 */
export function getMinimalPageObjectBoundingBox(
	objectModel: PageObjectModel,
	pageModel: PageModel,
	minVisiblePixels = MINIMUM_VISIBLE_PIXELS,
): BoundingBox {
	const minVisibleWidth = Math.min(
		minVisiblePixels,
		objectModel.width,
	);
	const minVisibleHeight = Math.min(
		minVisiblePixels,
		objectModel.height,
	);

	return {
		minX: minVisibleWidth - objectModel.width,
		maxX: pageModel.width - minVisibleWidth,
		minY: minVisibleHeight - objectModel.height,
		maxY: pageModel.height - minVisibleHeight,
	};
}

/**
 * Determines whether a page object supports photo cropping.
 *
 * Cropping changes the visible area inside a photo frame, so it remains
 * available for fixed and non-styleable objects. Stickers are excluded
 * because they do not represent user-croppable photos.
 *
 * @param objectModel The optional {@link PageObjectModel} to check.
 * @returns True when the object is an editable, non-sticker photo.
 */
export function isPhotoObjectCroppable(objectModel?: PageObjectModel | null): boolean {
	return !!(
		objectModel
		&& objectModel.type === 'photo'
		&& objectModel.editable
		&& !isStickerPageObject(objectModel)
	);
}

/**
 * Determines whether the provided {@link PageObjectModel} is a sticker.
 *
 * @param objectModel The {@link PageObjectModel} to check.
 * @returns True when the object references a sticker asset and lacks a linked photo id.
 */
export function isStickerPageObject(objectModel: PageObjectModel): boolean {
	if (objectModel.type !== 'photo') {
		return false;
	}

	/**
	 * Non-empty sources without `photoid` represent sticker assets stored
	 * in the theme data catalog.
	 * In the future we could add a new `type` to be `sticker`.
	 */
	if (
		typeof objectModel.source === 'string'
		&& objectModel.source.trim().length > 0
		&& (
			typeof objectModel.photoid === 'undefined'
			|| objectModel.photoid === null
		)
	) {
		return true;
	}

	return false;
}
